import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { JobDescriptionUploadDropzone } from "./JobDescriptionUploadDropzone";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface JobDescriptionUploadModalProps {
	isOpen: boolean;
	onClose: () => void;
	onProcessed: (extractedData: any) => void;
}

export const JobDescriptionUploadModal = ({
	isOpen,
	onClose,
	onProcessed
}: JobDescriptionUploadModalProps) => {
	const { t } = useTranslation();
	const [file, setFile] = useState<File | null>(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const [progress, setProgress] = useState(0);
	const [progressStatus, setProgressStatus] = useState('');

	useEffect(() => {
		if (isOpen && file) {
			// Trigger processing if a file is already selected when modal opens
			handleProcess();
		}
	}, [isOpen, file]);

	const simulateProgress = () => {
		setProgress(0);
		const interval = setInterval(() => {
			setProgress(prev => {
				if (prev >= 90) {
					clearInterval(interval);
					return 90;
				}
				return prev + 10;
			});

			// Update status message based on progress
			setProgressStatus(prev => {
				const progress = parseInt(prev.match(/\d+/)?.[0] || '0');
				if (progress < 30) return t("jobs.uploadModal.extractingText");
				if (progress < 60) return t("jobs.uploadModal.analyzing");
				if (progress < 90) return t("jobs.uploadModal.organizing");
				return t("jobs.uploadModal.finalizing");
			});
		}, 600);

		return () => clearInterval(interval);
	};

	const handleProcess = async () => {
		if (!file) {
			toast({
				title: t("jobs.uploadModal.noFileSelected"),
				description: t("jobs.uploadModal.pleaseUpload"),
				variant: "destructive"
			});
			return;
		}

		setIsProcessing(true);
		setProgress(20);
		setProgressStatus(t("jobs.uploadModal.processingFile"));

		try {
			const base64 = await fileToBase64(file);
    
			// Get file type without the dot
			const fileType = file.name.split('.').pop()?.toLowerCase();
			
			if (!fileType) {
			  throw new Error("Could not determine file type");
			}

			setProgress(30);

			const { data : fileContent, error: fileError } = await supabase.functions.invoke('get-document-content', {
				body: {
					documentBase64: base64,
					fileType: fileType
				}
			});

			setProgress(50);

			if (fileError) {
				console.error("Function error details:", fileError);
				toast({
					title: t("jobs.uploadModal.processingFailed"),
					description: fileError?.message || t("jobs.uploadModal.unexpectedError"),
					variant: "destructive"
				});
				throw new Error(fileError?.message || "Edge function failed");
			}

			if (!fileContent?.text) {
				throw new Error("No text content returned from edge function");
			}

			setProgress(70);

			const { data, error } = await supabase.functions.invoke('quick-scan-job-offer', {
				body: { 
					jobOfferText: fileContent.text
				},
			});

			setProgress(90);

			if (error) {
				console.error("Error processing file:", error);
				toast({
					title: t("jobs.uploadModal.processingFailed"),
					description: t("jobs.uploadModal.extractionFailed"),
					variant: "destructive"
				});
			} else {
				setProgress(100);
				setProgressStatus(t("jobs.uploadModal.complete"));
				setTimeout(() => {
					onProcessed(data);
					toast({
						title: t("jobs.uploadModal.processed"),
						description: t("jobs.uploadModal.extractionSuccess")
					});
				}, 500);
			}
		} catch (err) {
			console.error("Error reading file:", err);
			toast({
				title: t("jobs.uploadModal.processingFailed"),
				description: t("jobs.uploadModal.unexpectedError"),
				variant: "destructive"
			});
		} finally {
			setIsProcessing(false);
		}
	};

	const handleFileSelect = (selectedFile: File) => {
		setFile(selectedFile);
	};

	return (
		
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh]">
				<DialogHeader>
					<DialogTitle className="text-xl font-semibold">{t("jobs.uploadModal.title")}</DialogTitle>
				</DialogHeader>

				<div className="flex flex-col space-y-4 my-4">
					<div className="border rounded-sm h-[340px] overflow-hidden">

						<JobDescriptionUploadDropzone
							onFileSelect={handleFileSelect}
							isLoading={isProcessing}
							progress={progress}
							progressStatus={progressStatus}
							isFileUploadEnabled={true}
						/>

					</div>

					<div className="flex justify-end gap-2 pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							disabled={isProcessing}
						>
							{t("jobs.jobOfferModal.cancel")}
						</Button>
						<Button
							type="button"
							onClick={handleProcess}
							disabled={isProcessing || !file}
						>
							{isProcessing ? t("jobs.jobOfferModal.processing") : t("jobs.jobOfferModal.extractInfo")}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};


/**
 * Helper function to convert File to base64 string
 */
const fileToBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
	  const reader = new FileReader();
	  reader.readAsDataURL(file);
	  reader.onload = () => {
		if (typeof reader.result === 'string') {
		  // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
		  const base64 = reader.result.split(',')[1];
		  resolve(base64);
		} else {
		  reject(new Error("FileReader did not return a string"));
		}
	  };
	  reader.onerror = error => reject(error);
	});
  };
